/**
 * Target language code mapping for the questionnaire translation API.
 *
 * This module maps frontend locale strings (typically ISO 639-1/BCP 47 lowercase
 * codes from the getCountriesV2 GraphQL query) to uppercase target language codes
 * accepted by the fd-researcher-tools service.
 *
 * IMPORTANT: The canonical list of valid target codes lives in fd-researcher-tools
 * `src/domain/translation/inputs/questionnaire_languages.py` (TargetLanguageCode enum).
 * This TypeScript union must be kept in sync manually. If codes drift, the service
 * returns HTTP 422 for invalid targets. Consider codegen from the Python enum or
 * a contract test to prevent drift.
 *
 * Note: EN, PT, and ZH are source-only codes and are NOT valid targets.
 * Callers must use regional variants (EN-GB/EN-US, PT-BR/PT-PT, ZH-HANS/ZH-HANT).
 */

// Full target language catalogue (121 codes) as of fd-researcher-tools PR #30.
// Member order follows the Python enum (alphabetical by language name within groups).
export type TargetLanguageCode =
  // A
  | 'ACE' // Acehnese
  | 'AF' // Afrikaans
  | 'SQ' // Albanian
  | 'AR' // Arabic
  | 'AN' // Aragonese
  | 'HY' // Armenian
  | 'AS' // Assamese
  | 'AY' // Aymara
  | 'AZ' // Azerbaijani
  // B
  | 'BA' // Bashkir
  | 'EU' // Basque
  | 'BE' // Belarusian
  | 'BN' // Bengali
  | 'BHO' // Bhojpuri
  | 'BS' // Bosnian
  | 'BR' // Breton
  | 'BG' // Bulgarian
  | 'MY' // Burmese
  // C
  | 'YUE' // Cantonese
  | 'CA' // Catalan
  | 'CEB' // Cebuano
  | 'ZH-HANS' // Chinese (Simplified)
  | 'ZH-HANT' // Chinese (Traditional)
  | 'HR' // Croatian
  | 'CS' // Czech
  // D
  | 'DA' // Danish
  | 'PRS' // Dari
  | 'NL' // Dutch
  // E
  | 'EN-GB' // English (British)
  | 'EN-US' // English (American)
  | 'EO' // Esperanto
  | 'ET' // Estonian
  // F
  | 'FI' // Finnish
  | 'FR' // French
  | 'FR-CA' // French (Canadian)
  | 'FR-FR' // French (France)
  // G
  | 'GL' // Galician
  | 'KA' // Georgian
  | 'DE' // German
  | 'DE-DE' // German (Germany)
  | 'DE-CH' // German (Swiss)
  | 'EL' // Greek
  | 'GN' // Guarani
  | 'GU' // Gujarati
  // H
  | 'HT' // Haitian Creole
  | 'HA' // Hausa
  | 'HE' // Hebrew
  | 'HI' // Hindi
  | 'HU' // Hungarian
  // I
  | 'IS' // Icelandic
  | 'IG' // Igbo
  | 'ID' // Indonesian
  | 'GA' // Irish
  | 'IT' // Italian
  // J
  | 'JA' // Japanese
  | 'JV' // Javanese
  // K
  | 'PAM' // Kapampangan
  | 'KK' // Kazakh
  | 'GOM' // Konkani
  | 'KO' // Korean
  | 'KMR' // Kurdish (Kurmanji)
  | 'CKB' // Kurdish (Sorani)
  | 'KY' // Kyrgyz
  // L
  | 'LA' // Latin
  | 'LV' // Latvian
  | 'LN' // Lingala
  | 'LT' // Lithuanian
  | 'LMO' // Lombard
  | 'LB' // Luxembourgish
  // M
  | 'MK' // Macedonian
  | 'MAI' // Maithili
  | 'MG' // Malagasy
  | 'MS' // Malay
  | 'ML' // Malayalam
  | 'MT' // Maltese
  | 'MI' // Maori
  | 'MR' // Marathi
  | 'MN' // Mongolian
  // N
  | 'NE' // Nepali
  | 'NB' // Norwegian (Bokmål)
  // O
  | 'OC' // Occitan
  | 'OM' // Oromo
  // P
  | 'PAG' // Pangasinan
  | 'PS' // Pashto
  | 'FA' // Persian
  | 'PL' // Polish
  | 'PT-BR' // Portuguese (Brazilian)
  | 'PT-PT' // Portuguese (European)
  | 'PA' // Punjabi
  // Q
  | 'QU' // Quechua
  // R
  | 'RO' // Romanian
  | 'RU' // Russian
  // S
  | 'SA' // Sanskrit
  | 'SR' // Serbian
  | 'ST' // Sesotho
  | 'SCN' // Sicilian
  | 'SK' // Slovak
  | 'SL' // Slovenian
  | 'ES' // Spanish
  | 'ES-419' // Spanish (Latin American)
  | 'SU' // Sundanese
  | 'SW' // Swahili
  | 'SV' // Swedish
  // T
  | 'TL' // Tagalog
  | 'TG' // Tajik
  | 'TA' // Tamil
  | 'TT' // Tatar
  | 'TE' // Telugu
  | 'TH' // Thai
  | 'TS' // Tsonga
  | 'TN' // Tswana
  | 'TR' // Turkish
  | 'TK' // Turkmen
  // U
  | 'UK' // Ukrainian
  | 'UR' // Urdu
  | 'UZ' // Uzbek
  // V
  | 'VI' // Vietnamese
  // W
  | 'CY' // Welsh
  | 'WO' // Wolof
  // X
  | 'XH' // Xhosa
  // Y
  | 'YI' // Yiddish
  // Z
  | 'ZU' // Zulu

export class LanguageCodeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LanguageCodeError'
  }
}

export class AmbiguousLanguageError extends LanguageCodeError {
  readonly languageCode: string
  readonly alternatives: TargetLanguageCode[]

  constructor(languageCode: string, alternatives: TargetLanguageCode[]) {
    super(
      `Choose a regional variant for "${languageCode}": ${alternatives.join(
        ' or '
      )}.`
    )
    this.name = 'AmbiguousLanguageError'
    this.languageCode = languageCode
    this.alternatives = alternatives
  }
}

export class UnsupportedLanguageError extends LanguageCodeError {
  readonly languageCode: string

  constructor(languageCode: string) {
    super(
      `The language code "${languageCode}" is not supported by the translation service. Choose another target language.`
    )
    this.name = 'UnsupportedLanguageError'
    this.languageCode = languageCode
  }
}

// Source-only language codes that are NOT accepted as targets by the service's
// upstream provider. EN, PT, ZH must use regional variants: EN-GB/EN-US,
// PT-BR/PT-PT, ZH-HANS/ZH-HANT.
const ambiguousLanguageCodes: Partial<Record<string, TargetLanguageCode[]>> = {
  en: ['EN-GB', 'EN-US'],
  pt: ['PT-PT', 'PT-BR'],
  zh: ['ZH-HANS', 'ZH-HANT']
}

/**
 * Map of lowercase frontend locale codes to uppercase target language codes.
 *
 * This covers codes we expect from the getCountriesV2 GraphQL query as well as
 * regional variants. Not every target code has a mapping — we only include
 * locales that the Focaldata frontend actually offers via country/locale data.
 * Unknown codes fall through to UnsupportedLanguageError.
 *
 * IMPORTANT: The ~52 codes present in TargetLanguageCode but ABSENT from this map
 * are INTENTIONAL. Those are valid service targets (e.g. ACE, AN, AY, BA, BHO, etc.)
 * that the Focaldata panel-supplier locale catalogue does not offer. When a frontend
 * code is not in this map, toTargetLanguageCode() throws UnsupportedLanguageError —
 * that behaviour is deliberate, not a bug like the cy→CY fix. Do NOT "fix" this by
 * adding all 122 codes; the map should only grow when getCountriesV2 adds new locales.
 *
 * Contract invariant: every VALUE in this map must be a valid service code (tested).
 *
 * DeepL support was verified empirically using translation requests, rather than
 * inferred only from get_target_languages(), which under-reports support. AZ, BS,
 * HE, HI, HR, IS, MK, MS, MT, SQ, SR, and TH translate successfully even though
 * several are absent from that listing endpoint. AM and LO are the only offered
 * frontend locales verified to be rejected by DeepL, so they intentionally remain
 * absent and produce UnsupportedLanguageError.
 */
export const languageCodeMap: Partial<Record<string, TargetLanguageCode>> = {
  // Simple ISO 639-1 codes (common locales from panel suppliers)
  af: 'AF',
  ar: 'AR',
  az: 'AZ',
  be: 'BE',
  bg: 'BG',
  bn: 'BN',
  bs: 'BS',
  ca: 'CA',
  cs: 'CS',
  cy: 'CY',
  da: 'DA',
  de: 'DE',
  el: 'EL',
  eo: 'EO',
  es: 'ES',
  et: 'ET',
  eu: 'EU',
  fa: 'FA',
  fi: 'FI',
  fr: 'FR',
  ga: 'GA',
  gl: 'GL',
  gu: 'GU',
  he: 'HE',
  hi: 'HI',
  hr: 'HR',
  hu: 'HU',
  hy: 'HY',
  id: 'ID',
  is: 'IS',
  it: 'IT',
  ja: 'JA',
  ka: 'KA',
  kk: 'KK',
  ko: 'KO',
  ky: 'KY',
  la: 'LA',
  lt: 'LT',
  lv: 'LV',
  mk: 'MK',
  ml: 'ML',
  mn: 'MN',
  mr: 'MR',
  ms: 'MS',
  mt: 'MT',
  my: 'MY',
  nb: 'NB',
  ne: 'NE',
  nl: 'NL',
  pa: 'PA',
  pl: 'PL',
  ro: 'RO',
  ru: 'RU',
  sk: 'SK',
  sl: 'SL',
  sq: 'SQ',
  sr: 'SR',
  sv: 'SV',
  sw: 'SW',
  ta: 'TA',
  te: 'TE',
  tg: 'TG',
  th: 'TH',
  tl: 'TL',
  tr: 'TR',
  uk: 'UK',
  ur: 'UR',
  uz: 'UZ',
  vi: 'VI',

  // Regional variants (hyphenated BCP 47 codes)
  // English
  'en-gb': 'EN-GB',
  'en-us': 'EN-US',
  // Spanish
  'es-419': 'ES-419',
  // Portuguese
  'pt-pt': 'PT-PT',
  'pt-br': 'PT-BR',
  // Chinese (simplified/traditional)
  'zh-hans': 'ZH-HANS',
  'zh-hant': 'ZH-HANT',
  // French
  'fr-ca': 'FR-CA',
  'fr-fr': 'FR-FR',
  // German
  'de-de': 'DE-DE',
  'de-ch': 'DE-CH'
}

/**
 * Convert a frontend locale code to the corresponding target language code.
 *
 * @param frontendLanguageCode - Locale string from the frontend (e.g. 'fr', 'en-gb')
 * @returns The uppercase target language code
 * @throws AmbiguousLanguageError if the code requires a regional variant (en, pt, zh)
 * @throws UnsupportedLanguageError if the code is not recognised
 */
export const toTargetLanguageCode = (
  frontendLanguageCode: string
): TargetLanguageCode => {
  const normalizedCode = frontendLanguageCode.trim().toLowerCase()
  const alternatives = ambiguousLanguageCodes[normalizedCode]

  if (alternatives) {
    throw new AmbiguousLanguageError(normalizedCode, alternatives)
  }

  const mappedCode = languageCodeMap[normalizedCode]
  if (!mappedCode) {
    throw new UnsupportedLanguageError(frontendLanguageCode)
  }

  return mappedCode
}

/**
 * All target language codes recognised by the questionnaire API.
 * Exported for contract testing against the service's enum.
 */
export const ALL_TARGET_LANGUAGE_CODES: readonly TargetLanguageCode[] = [
  'ACE',
  'AF',
  'SQ',
  'AR',
  'AN',
  'HY',
  'AS',
  'AY',
  'AZ',
  'BA',
  'EU',
  'BE',
  'BN',
  'BHO',
  'BS',
  'BR',
  'BG',
  'MY',
  'YUE',
  'CA',
  'CEB',
  'ZH-HANS',
  'ZH-HANT',
  'HR',
  'CS',
  'DA',
  'PRS',
  'NL',
  'EN-GB',
  'EN-US',
  'EO',
  'ET',
  'FI',
  'FR',
  'FR-CA',
  'FR-FR',
  'GL',
  'KA',
  'DE',
  'DE-DE',
  'DE-CH',
  'EL',
  'GN',
  'GU',
  'HT',
  'HA',
  'HE',
  'HI',
  'HU',
  'IS',
  'IG',
  'ID',
  'GA',
  'IT',
  'JA',
  'JV',
  'PAM',
  'KK',
  'GOM',
  'KO',
  'KMR',
  'CKB',
  'KY',
  'LA',
  'LV',
  'LN',
  'LT',
  'LMO',
  'LB',
  'MK',
  'MAI',
  'MG',
  'MS',
  'ML',
  'MT',
  'MI',
  'MR',
  'MN',
  'NE',
  'NB',
  'OC',
  'OM',
  'PAG',
  'PS',
  'FA',
  'PL',
  'PT-BR',
  'PT-PT',
  'PA',
  'QU',
  'RO',
  'RU',
  'SA',
  'SR',
  'ST',
  'SCN',
  'SK',
  'SL',
  'ES',
  'ES-419',
  'SU',
  'SW',
  'SV',
  'TL',
  'TG',
  'TA',
  'TT',
  'TE',
  'TH',
  'TS',
  'TN',
  'TR',
  'TK',
  'UK',
  'UR',
  'UZ',
  'VI',
  'CY',
  'WO',
  'XH',
  'YI',
  'ZU'
] as const
